/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { addToChainAfter, addToChainBefore, addToEndOfChain } from "@insite/client-framework/HandlerCreator";
import { chain as GetAutocompleteModelChain } from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import { chain as SubscribeChain } from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import { chain as SubmitContactFormChain } from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SubmitContactForm";
import { chain as SignInChain, DispatchCompleteSignIn } from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { chain as AddAccountChain } from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { chain as AddToWishListChain } from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { chain as AddToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { chain as RemoveCartLineChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import { chain as PlaceOrderChain } from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import {
    chain as DeleteWishListLineChain,
    ResetWishListData,
} from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/DeleteWishListLine";

// eslint-disable-next-line spire/fenced-imports
import mixpanel from "mixpanel-browser";

const identify: any = (props: any) => {
    if (props && props.authenticatedSession) {
        mixpanel.identify(`${props?.authenticatedSession?.firstName} ${props?.authenticatedSession?.lastName}`);
    }
};

export const _MpAddProductToCart: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }

    const currentPage = getCurrentPage(props.getState());

    mixpanel.track("Add to Cart", {
        Origin: currentPage.name,
        Quantity: props.apiResult.qtyOrdered,
        Items: [
            {
                name: props.apiResult.shortDescription,
                id: props.apiResult.erpNumber,
                price: props.apiResult.pricing.unitNetPrice,
                brand: props.apiResult.brand?.name || "",
                category: props.apiResult.properties["category"],
            },
        ],
    });
};

export const _MpRemoveCartLine: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }

    const currentPage = getCurrentPage(props.getState());
    const cart = getCurrentCartState(props.getState()).value;
    const cartLine = cart?.cartLines?.find(c => c.id === props.apiParameter.cartLineId);

    if (cartLine) {
        mixpanel.track("Remove from Cart", {
            Origin: currentPage.name,
            Quantity: cartLine.qtyOrdered,
            Items: [
                {
                    name: cartLine.shortDescription,
                    id: cartLine.erpNumber,
                    price: cartLine.pricing?.unitNetPrice,
                    brand: cartLine.brand?.name || "",
                    category: cartLine.properties["category"],
                    quantity: cartLine.qtyOrdered,
                },
            ],
        });
    }
};

export const _MpPlaceOrder: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }

    mixpanel.track("Purchase", {
        "Order Number": props.apiResult.cart.orderNumber,
        Total: props.apiResult.cart.orderGrandTotal,
        Tax: props.apiResult.cart.totalTax,
        Shipping: props.apiResult.cart.shippingAndHandling,
        Discount: 0,
        "Customer Details": props.apiResult.billTo.companyName,
    });

    const cartLines = props?.apiParameter?.cart?.cartLines || [];

    cartLines.forEach((cartLine: any) => {
        mixpanel.track("Item Purchased", {
            Quantity: cartLine.qtyOrdered,
            Name: cartLine.shortDescription,
            SKU: cartLine.erpNumber,
            "Brand/Supplier": cartLine.brand?.name,
            Category: cartLine.properties?.category,
            "Purchased Price": cartLine.pricing?.actualPriceDisplay,
        });
    });

    mixpanel.people.track_charge(props.apiResult.cart.orderGrandTotal);
    mixpanel.people.increment("Revenue", props.apiResult.cart.orderGrandTotal);
    mixpanel.people.set("Last Item Purchase", new Date());
    identify(props);
};

export const _MpAddToShoppingList: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }

    const product = getProductState(props.getState(), props.parameter.productInfos[0].productId).value as any;

    if (product) {
        mixpanel.track("Add to Shopping List", {
            Quantity: props.parameter.productInfos[0].qtyOrdered,
            Items: [
                {
                    name: product.productTitle,
                    id: props.parameter.productInfos[0].productId,
                    price: props.parameter.productInfos[0].pricing?.unitNetPrice || 0,
                    brand: product.brand,
                    category: product.properties["category"],
                    unitOfMeasure: props.parameter.productInfos[0].unitOfMeasure,
                },
            ],
        });
        identify(props);
    }
};

export const _MpRemoveFromShoppingList: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }

    const wishListState = getWishListState(props.getState(), props.parameter.wishListId).value;
    const wishListLine = wishListState?.wishListLineCollection?.find(
        (line: any) => line.id === props.parameter.wishListLineId,
    ) as any;

    if (wishListLine) {
        mixpanel.track("Remove from Shopping List", {
            Quantity: wishListLine.qtyOrdered,
            Items: [
                {
                    name: wishListLine.shortDescription,
                    id: wishListLine.erpNumber,
                    price: wishListLine.pricing.unitNetPrice,
                    brand: wishListLine.brand?.name || "",
                    category: wishListLine.properties["category"],
                    unitOfMeasure: wishListLine.unitOfMeasure,
                },
            ],
        });
        identify(props);
    }
};

export const _MpSignIn: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }

    mixpanel.track("Login", {
        "ERP ID": props.authenticatedSession?.properties["contactId"] || "",
        "Customer Number": props.authenticatedSession?.billTo?.customerNumber,
        Email: props.authenticatedSession?.email,
        Segment: props.authenticatedSession?.properties["customerSegment"] || "",
        "Company Name": props.authenticatedSession?.properties["customerCompanyName"] || "",
        Zipcode: props.authenticatedSession?.properties["customerPostalCode"] || "",
    });
    mixpanel.people.set({
        $name: `${props.authenticatedSession?.firstName} ${props.authenticatedSession?.lastName}`,
        $email: props.authenticatedSession?.email,
        "ERP ID": props.authenticatedSession?.properties["contactId"] || "",
        Segment: props.authenticatedSession?.properties["customerSegment"] || "",
        "Company Name": props.authenticatedSession?.properties["customerCompanyName"] || "",
        Zipcode: props.authenticatedSession?.properties["customerPostalCode"] || "",
    });
    identify(props);
};

export const _MpAddAccount: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }
    mixpanel.track("Registration", {
        "First Name": props.apiParameter.account.firstName,
        "Last Name": props.apiParameter.account.lastName,
        "Company Number": props.apiParameter.account.properties.CompanyNumber || "",
        "Company Name": props.apiParameter.account.properties.CompanyName,
        Phone: props.apiParameter.account.properties.Phone,
        Email: props.apiParameter.account.email,
    });
    identify(props);
};

export const _MpSubscribe: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }
    mixpanel.track("Newsletter Sign-up", {
        Email: props.apiParameter.emailAddress,
    });
    identify(props);
};

export const _MpSubmitContactForm: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
        return;
    }
    mixpanel.track("Contact Form Submission", {
        ...props.apiParameter,
    });
    identify(props);
};

export const _MpSendSearchTracking: any = (props: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!(window as any)?.hasOwnProperty("mixpanelInitialized")) {
        return;
    }

    // eslint-disable-next-line no-prototype-builtins
    if (props?.hasOwnProperty("apiResult")) {
        const products = props.apiResult.products.length === 0 ? null : <any>[];

        props.apiResult.products.map((product: any) => products?.push(product["title"]));
        mixpanel.track("Search", {
            Term: props.parameter.query,
            Count: props.apiResult.products.length,
            Products: products,
        });
        identify(props);
    }
};

addToEndOfChain(AddToCartChain, _MpAddProductToCart);
addToEndOfChain(RemoveCartLineChain, _MpRemoveCartLine);
addToEndOfChain(PlaceOrderChain, _MpPlaceOrder);
addToEndOfChain(AddToWishListChain, _MpAddToShoppingList);
addToChainBefore(DeleteWishListLineChain, ResetWishListData, _MpRemoveFromShoppingList);
addToEndOfChain(AddAccountChain, _MpAddAccount);
addToChainAfter(SignInChain, DispatchCompleteSignIn, _MpSignIn);
addToEndOfChain(SubscribeChain, _MpSubscribe);
addToEndOfChain(SubmitContactFormChain, _MpSubmitContactForm);
addToEndOfChain(GetAutocompleteModelChain, _MpSendSearchTracking);

/* eslint-disable */
(function(window) {
    (window as any)._MpCheckoutFunnel = (step: any) => {
        // eslint-disable-next-line no-prototype-builtins
        if (!(window as any).hasOwnProperty("mixpanelInitialized")) {
            return;
        }
        mixpanel.track("Checkout", {
            "Step": step
        });
    };

	(window as any)._MpProductCarouselClick = (product: any) => {
        if(!(window as any).hasOwnProperty("mixpanelInitialized")) return;
        
        const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
        const imagePath =
            "product" in product
                ? product.product.mediumImagePath || product.product.smallImagePath
                : product.smallImagePath;
         mixpanel.track("Carousels", {
            Index: "product" in product ? product.productInfo["erpNumber"] : product["erpNumber"],
            "Destination URL": productDetailPath,
            "Image URL": imagePath,
        });
    };

    (window as any)._MpSlideShowClick = (index: any,destUrl : string,imagePath: string) => {
        if(!(window as any).hasOwnProperty("mixpanelInitialized")) return;
        
        mixpanel.track("Carousels", {
            Index: index,
            "Destination URL": destUrl,
            "Image URL": imagePath,
        });
    };
})(typeof window != 'undefined' ? window : {})
/* eslint-enable */
